import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { FormatNumberPipe } from 'src/app/shared/pipes/format-number.pipe';
import { FormatRelativeDatetimePipe } from 'src/app/shared/pipes/format-relative-datetime.pipe';
import { ThaiDateTimePipe } from 'src/app/shared/pipes/thai-datetime.pipe';
import {
    PACKAGE_SOURCE_DESCRIPTIONS,
    PACKAGE_SOURCE_LABELS,
    PACKAGE_STAGE_LABELS,
    RETURN_TYPE_LABELS,
    ReturnPackage,
} from 'src/app/shared/services/returns/return-package';
import { ReceiptMark } from 'src/app/shared/services/returns/return-receipt.service';
import {
    LnwshopSaleService,
    LnwshopSaleState,
} from 'src/app/shared/services/returns/lnwshop-sale.service';
import {
    RestockAssessment,
    assessRestock,
} from 'src/app/shared/services/returns/lnwshop-restock';

// Everything known about one parcel coming back — a buyer's return or a
// failed delivery — in one popup: what kind it is and what that means, the
// order and codes, where the parcel is, the shop's own confirmation, the
// items, and the courier's history where the platform gives one. Opened
// from the type badge on the returns and inspection pages; a page drops one
// instance in its template and calls open().
@Component({
    selector: 'app-return-detail-popup',
    templateUrl: './return-detail-popup.component.html',
    styleUrls: ['./return-detail-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxButtonComponent,
        DxPopupComponent,
        FormatNumberPipe,
        FormatRelativeDatetimePipe,
        ThaiDateTimePipe,
    ],
})
export class ReturnDetailPopupComponent {
    private cdref = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);
    private lnwshopSaleService = inject(LnwshopSaleService);

    // A SKU in the item list was clicked: the page opens its product card.
    @Output() skuClick = new EventEmitter<string>();
    // The popup closed, whichever way — the returns page puts focus back in
    // its scan box.
    @Output() hidden = new EventEmitter<void>();

    visible = false;
    pkg: ReturnPackage | null = null;
    receipt: ReceiptMark | null = null;
    // Whether lnwshop puts the units back by itself: the platform rule at
    // once, then lnwshop's own crawl once it answers.
    restock: RestockAssessment | null = null;
    checkingRestock = false;
    // The check for what is on screen; a reopen or close cancels it.
    private restockLookup?: Subscription;

    readonly sourceLabels = PACKAGE_SOURCE_LABELS;
    readonly sourceDescriptions = PACKAGE_SOURCE_DESCRIPTIONS;
    readonly stageLabels = PACKAGE_STAGE_LABELS;

    // `known`: what the caller already learnt from lnwshop about this order
    // (the inspection page has it), shown at once and kept if this popup's
    // own check fails — so the popup never contradicts the page.
    open(
        pkg: ReturnPackage,
        receipt: ReceiptMark | null = null,
        known?: {
            sale: LnwshopSaleState | undefined;
            crawledAt: Moment | null;
        },
    ): void {
        this.restockLookup?.unsubscribe();
        this.pkg = pkg;
        this.receipt = receipt;
        this.restock = assessRestock(
            pkg,
            known?.sale,
            known?.crawledAt ?? null,
        );
        this.checkingRestock = true;
        this.visible = true;
        this.cdref.markForCheck();
        this.restockLookup = this.lnwshopSaleService
            .lookup([pkg.orderNo])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((lookup) => {
                this.checkingRestock = false;
                if (!lookup.failed || !known?.sale) {
                    this.restock = assessRestock(
                        pkg,
                        lookup.states[pkg.orderNo],
                        lookup.crawledAt,
                    );
                }
                this.cdref.markForCheck();
            });
    }

    close(): void {
        if (this.visible) {
            this.visible = false;
            this.cdref.markForCheck();
        }
    }

    // The request type in Thai, or the platform's own code when unmapped.
    get returnTypeLabel(): string {
        const type = this.pkg?.returnType ?? '';
        return RETURN_TYPE_LABELS[type] ?? type;
    }

    // What the package's start date means for its kind: a return is dated
    // by its request; a failed delivery by when the platform recorded it
    // coming back (the courier turning it around, or — TikTok — its
    // arrival), since nothing earlier is recorded.
    get createdLabel(): string {
        return this.pkg?.source === 'failed_delivery'
            ? 'แพลตฟอร์มบันทึกตีกลับ'
            : 'ยื่นคำขอคืน';
    }

    get title(): string {
        return this.pkg
            ? `${PACKAGE_SOURCE_LABELS[this.pkg.source]} · ${this.pkg.storeTitle} · ${this.pkg.orderNo}`
            : '';
    }

    get totalUnits(): number {
        return (this.pkg?.items ?? []).reduce((sum, item) => sum + item.qty, 0);
    }

    // Every way the popup closes (ปิด, the X, an outside click) ends here:
    // a check still running belongs to what was on screen, so it stops.
    onHidden(): void {
        this.restockLookup?.unsubscribe();
        this.checkingRestock = false;
        this.hidden.emit();
    }
}
